'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useLanguage } from '@/contexts/LanguageContext';
import { products } from '@/data/products';

const FALLBACK_IMAGE = '/assets/images/no_image.png';

function ProductCardImage({ src, alt }: { src: string; alt: string }) {
  const [imgSrc, setImgSrc] = useState(src || FALLBACK_IMAGE);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div
      className="relative w-full flex items-center justify-center overflow-hidden bg-white"
      style={{ height: '220px' }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <Image
        src={imgSrc}
        alt={alt || 'Product image'}
        fill
        className={`object-cover object-center transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImgSrc(FALLBACK_IMAGE);
          setIsLoading(false);
        }}
        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
      />
    </div>
  );
}

function CategoryCardImage({ src, alt }: { src: string; alt: string }) {
  const [imgSrc, setImgSrc] = useState(src || FALLBACK_IMAGE);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div
      className="relative w-full overflow-hidden bg-white"
      style={{ height: '260px' }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: '#ffffff' }}>
          <div className="w-6 h-6 border-2 border-primary/40 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <Image
        src={imgSrc}
        alt={alt}
        fill
        className={`object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImgSrc(FALLBACK_IMAGE);
          setIsLoading(false);
        }}
        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
      />
    </div>
  );
}

export default function CatalogSection() {
  const { t, language } = useLanguage();

  const TARGET_CATEGORIES = [
    'Céphalopodes / Crustacés',
    'Soles',
    'Mérous',
    'Courbines / Dorades / Pagres',
    'Démersaux divers',
    'Pélagiques / Chinchards',
  ];

  const featuredProducts = TARGET_CATEGORIES.map(
    (cat) => products.find((p) => p.category === cat)
  ).filter(Boolean) as typeof products;

  const viewMoreLabel =
    language === 'fr' ?'Voir Plus de Produits'
      : language === 'ar' ?'عرض المزيد من المنتجات' :'View More Products';

  const categoryCards = [
    {
      titles: { en: 'CRUSTACEANS', fr: 'CRUSTACÉS', ar: 'القشريات' },
      image: '/assets/categories/magnific__ultrarealistic-commercial-seafood-photography-feat__25269.png',
      imageAlt: 'Crustaceans',
      species: [
        { en: 'Lobster', fr: 'Langouste', ar: 'جراد البحر' },
        { en: 'Shrimp', fr: 'Crevette', ar: 'الجمبري' },
      ],
    },
    {
      titles: { en: 'DEMERSAL FISH', fr: 'POISSONS DÉMERSAUX', ar: 'الأسماك القاعية' },
      image: '/assets/categories/magnific__ultrarealistic-commercial-seafood-photography-feat__25268.png',
      imageAlt: 'Demersal Fish',
      species: [
        { en: 'Sea Bream', fr: 'Dorade', ar: 'الدنيس' },
        { en: 'Grouper', fr: 'Mérou', ar: 'الهامور' },
        { en: 'Sole', fr: 'Sole', ar: 'سمك موسى' },
        { en: 'Red Mullet', fr: 'Rouget', ar: 'السلطان إبراهيم' },
        { en: 'Dentex', fr: 'Dentex', ar: 'الدنتاكس' },
      ],
    },
    {
      titles: { en: 'PELAGIC FISH', fr: 'POISSONS PÉLAGIQUES', ar: 'الأسماك السطحية' },
      image: '/assets/categories/magnific__ultrarealistic-commercial-seafood-photography-feat__25266.png',
      imageAlt: 'Pelagic Fish',
      species: [
        { en: 'Mackerel', fr: 'Maquereau', ar: 'الماكريل' },
        { en: 'Sardine', fr: 'Sardine', ar: 'السردين' },
        { en: 'Horse Mackerel', fr: 'Chinchard', ar: 'الشنشار' },
      ],
    },
    {
      titles: { en: 'CEPHALOPODS', fr: 'CÉPHALOPODES', ar: 'الرأسقدميات' },
      image: '/assets/categories/magnific__ultrarealistic-commercial-seafood-photography-feat__25264.png',
      imageAlt: 'Cephalopods',
      species: [
        { en: 'Octopus', fr: 'Poulpe', ar: 'الأخطبوط' },
        { en: 'Squid', fr: 'Calamar', ar: 'الحبار' },
        { en: 'Cuttlefish', fr: 'Seiche', ar: 'السيبيا' },
      ],
    },
  ];

  const typeLabel = language === 'ar' ? 'النوع :' : 'Type / Type :';
  const originLabel = language === 'ar' ? 'الأصل :' : 'Origin / Origine :';
  const typeValue = language === 'ar' ? 'بري / Sauvage' : 'Wild / Sauvage';
  const originValue = language === 'ar' ? 'موريتانيا / Mauritanie' : 'Mauritania / Mauritanie';

  return (
    <section id="catalog" className="py-24 px-6 md:px-12 bg-muted">
      <div className="max-w-7xl mx-auto">

        {/* OUR CATEGORIES sub-section */}
        <div className="mb-20">

          {/* Section Title */}
          <div className="text-center mb-8 reveal">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('catalog.categories.title')}
            </h2>
          </div>

          {/* 4 Category Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categoryCards.map((cat, index) => (
              <div
                key={index}
                className="reveal flex flex-col rounded-2xl overflow-hidden shadow-sm"
                style={{ backgroundColor: '#ffffff', transitionDelay: `${index * 80}ms` }}
              >
                {/* Card Header */}
                <div
                  className="px-4 py-3 text-center"
                  style={{ backgroundColor: '#0B4F6C' }}
                >
                  <p className="text-white font-bold text-sm md:text-base tracking-wider uppercase leading-tight">
                    {cat.titles[language as 'en' | 'fr' | 'ar'] || cat.titles.en}
                  </p>
                </div>

                {/* Category Image */}
                <div style={{ backgroundColor: '#ffffff' }}>
                  <CategoryCardImage src={cat.image} alt={cat.imageAlt} />
                </div>

                {/* Species Bullet List */}
                <div className="px-4 py-4 flex-1" style={{ backgroundColor: '#ffffff' }}>
                  <ul className="space-y-1.5">
                    {cat.species.map((sp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        <span className="font-medium text-foreground">
                          {sp[language as 'en' | 'fr' | 'ar'] || sp.en}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
            {/* {t('catalog.badge')} */}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('catalog.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('catalog.subtitle')}
          </p>
          <p className="text-sm font-semibold text-primary mt-4">
            {/* {t('catalog.ungutted')} */}
          </p>
        </div>

        {/* Products Grid - 6 featured products */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredProducts?.map((product, index) => {
            const productNumber = product?.id
              ? parseInt(product.id.replace('product_', ''), 10)
              : index + 1;

            return (
              <Link
                key={product?.id}
                href={`/products/${product?.slug}`}
                className="reveal card-hover bg-card border border-border rounded-2xl overflow-hidden group flex flex-col shadow-sm hover:shadow-ocean transition-shadow duration-300"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {/* 1. TOP HEADER BAR */}
                <div className="px-4 py-2.5" style={{ backgroundColor: '#0B4F6C' }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-white/70 whitespace-nowrap">{typeLabel}</span>
                    <span className="text-xs font-bold text-white text-right">{typeValue}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-xs text-white/70 whitespace-nowrap">{originLabel}</span>
                    <span className="text-xs font-bold text-white text-right">{originValue}</span>
                  </div>
                </div>

                {/* 2. MIDDLE INFO AREA */}
                <div className="px-4 py-3 bg-card flex flex-col" style={{ minHeight: '80px' }}>
                  <p className="text-sm font-bold text-foreground uppercase tracking-wide leading-snug line-clamp-2">
                    {productNumber}. {product?.scientificName?.toUpperCase()}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                    {language === 'fr'
                      ? `${product?.commonNameFR} / ${product?.commonNameEN}`
                      : language === 'ar'
                      ? `${product?.commonNameEN} / ${product?.commonNameFR}`
                      : `${product?.commonNameEN} / ${product?.commonNameFR}`}
                  </p>
                </div>

                {/* 3. BOTTOM IMAGE AREA */}
                <div className="flex-1 overflow-hidden">
                  <ProductCardImage
                    src={product?.image}
                    alt={product?.alt || product?.scientificName}
                  />
                </div>
              </Link>
            );
          })}
        </div>

        {/* View More Products Button */}
        <div className="flex justify-center mt-12">
          <Link
            href="/products"
            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 hover:shadow-lg active:scale-95 transition-all duration-200 shadow-md group"
          >
            {viewMoreLabel}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  );
}
